import { motion } from "framer-motion";
import { ArrowUpRight, Award, BookOpen, Briefcase, CheckCircle2, ChevronRight, GraduationCap, Sparkles, Star, TrendingUp, Users, Workflow, Zap } from "lucide-react";
import { PROFILE, AI_MATCHING, OPPORTUNITIES, COURSES, DASHBOARD_STATS, shortIdr } from "../data/kabelul";

const Stat = ({ icon: Icon, label, value, note, accent }) => (
  <div className="k-stat" data-testid={`dashboard-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
    <div className={`k-stat-icon ${accent}`}><Icon size={18} /></div>
    <div>
      <p>{label}</p>
      <strong>{value}</strong>
      <small>{note}</small>
    </div>
  </div>
);

const Flow = () => {
  const steps = [
    { icon: BookOpen, label: "Learn", tone: "blue", detail: "6 courses" },
    { icon: Award, label: "Qualify", tone: "emerald", detail: "5 verified skills" },
    { icon: Briefcase, label: "Work", tone: "amber", detail: "12 opportunities" },
    { icon: Star, label: "Grow", tone: "green", detail: "4.8 reputation" },
  ];
  return (
    <div className="k-flow" data-testid="dashboard-ecosystem-flow">
      {steps.map((s, i) => (
        <div className="k-flow-item" key={s.label}>
          <div className={`k-flow-icon ${s.tone}`}><s.icon size={19} /></div>
          <div>
            <b>{s.label}</b>
            <small>{s.detail}</small>
          </div>
          {i < steps.length - 1 && <ChevronRight size={16} className="k-flow-arrow" />}
        </div>
      ))}
    </div>
  );
};

export default function DashboardPage({ setPage }) {
  const topOpps = OPPORTUNITIES.filter((o) => o.eligible).slice(0, 3);
  const recCourse = COURSES.find((c) => c.recommended);

  return (
    <div className="page-inner">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">WEDNESDAY, 19 AUGUST 2026 · BANDUNG</p>
          <h1>Selamat datang, {PROFILE.name.split(" ")[0]} <span>✦</span></h1>
          <p className="subtitle">Your qualifications unlocked <b style={{ color: "var(--green)" }}>{AI_MATCHING.eligible} new opportunities</b> this week. Here's what's next.</p>
        </div>
        <button data-testid="view-opportunities-cta" className="primary-button" onClick={() => setPage("opportunities")}>
          <Briefcase size={16} /> View opportunities
        </button>
      </motion.div>

      <Flow />
      <p className="k-flow-caption">Learn new skills. Prove your qualifications. Unlock better work. Build your reputation — every completed job feeds back into the next opportunity.</p>

      <div className="stats-grid" style={{ marginTop: 22 }}>
        <Stat icon={Briefcase} label="Eligible opportunities" value={AI_MATCHING.eligible} note={`${AI_MATCHING.invitesReceived} direct invites this week`} accent="green" />
        <Stat icon={GraduationCap} label="Active learning" value={`${DASHBOARD_STATS.activeCourses} courses`} note={`Overall progress ${DASHBOARD_STATS.learningProgress}%`} accent="amber" />
        <Stat icon={CheckCircle2} label="Verified skills" value={DASHBOARD_STATS.verifiedSkills} note={`Tier ${PROFILE.tierLevel}/${PROFILE.tierMax} — ${PROFILE.tier}`} accent="blue" />
        <Stat icon={Star} label="Reputation" value={`${DASHBOARD_STATS.reputationScore} ★`} note={`${DASHBOARD_STATS.reputationJobs} completed jobs`} accent="red" />
      </div>

      <div className="content-grid">
        <section className="panel showcase-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker"><Sparkles size={11} style={{ verticalAlign: -1, marginRight: 4 }} /> KABELUL AI MATCH</div>
              <h2>Top opportunities for your Qualification Passport</h2>
              <p className="muted">Ranked by verified-skill match and location.</p>
            </div>
            <button data-testid="dashboard-view-all-opportunities" className="quiet-button" onClick={() => setPage("opportunities")}>
              See all <ArrowUpRight size={13} />
            </button>
          </div>

          <div className="k-opp-list">
            {topOpps.map((o, i) => (
              <button className="k-opp-row" key={o.id} onClick={() => setPage("opportunities")} data-testid={`dashboard-opportunity-${i}`}>
                <div className={`k-opp-avatar ${o.tone}`}>{o.companyInitials}</div>
                <div className="k-opp-body">
                  <b>{o.title}</b>
                  <small>{o.company} · {o.location} · <span className="k-tag">{o.type}</span></small>
                </div>
                <div className="k-opp-side">
                  <div className="k-match-pill"><Zap size={11} /> {o.matchScore}% match</div>
                  <b>{shortIdr(o.budget)}</b>
                </div>
              </button>
            ))}
          </div>

          <div className="k-unlock-box">
            <div className="k-unlock-icon"><TrendingUp size={17} /></div>
            <div>
              <small>UNLOCK MORE OPPORTUNITIES</small>
              <b>Complete <em>{recCourse?.title}</em> to unlock <strong>{recCourse?.unlocks} additional opportunities</strong>.</b>
            </div>
            <button data-testid="dashboard-open-course" className="dark-button" onClick={() => setPage("learning")}>
              Start course <ChevronRight size={14} />
            </button>
          </div>
        </section>

        <section className="panel activity-panel">
          <div className="panel-head">
            <div>
              <div className="section-kicker">YOUR PASSPORT</div>
              <h3>{PROFILE.tier}</h3>
            </div>
            <button data-testid="dashboard-view-passport" className="quiet-button" onClick={() => setPage("qualifications")}>Open <ArrowUpRight size={13} /></button>
          </div>

          <div className="k-passport-card">
            <img src={PROFILE.photo} alt={PROFILE.name} className="k-avatar-lg" />
            <div>
              <b>{PROFILE.name}</b>
              <small>{PROFILE.title}</small>
              <small className="k-passport-id">{PROFILE.passportId}</small>
            </div>
          </div>

          <div className="k-tier-progress">
            <div className="k-tier-track">
              {Array.from({ length: PROFILE.tierMax }).map((_, i) => (
                <span key={i} className={i < PROFILE.tierLevel ? "on" : ""} />
              ))}
            </div>
            <small>Tier {PROFILE.tierLevel} of {PROFILE.tierMax} — 2 more certifications to reach Expert</small>
          </div>

          <div className="k-mini-row">
            <div><small>RATING</small><b>{PROFILE.rating} ★</b></div>
            <div><small>JOBS DONE</small><b>{PROFILE.completedJobs}</b></div>
            <div><small>SKILLS</small><b>{PROFILE.skills.filter(s => s.verified).length} verified</b></div>
          </div>

          <div className="k-next-step">
            <Workflow size={17} />
            <div>
              <small>ECOSYSTEM FLOW</small>
              <b>Businesses can discover you through Talent search & Sourcing RFQs.</b>
            </div>
          </div>
        </section>
      </div>

      <section className="section-block">
        <div className="section-title">
          <div>
            <div className="section-kicker">FOR BUSINESSES · TWO-SIDED ECOSYSTEM</div>
            <h2>KABELUL for teams</h2>
          </div>
        </div>
        <div className="k-biz-grid">
          <button className="k-biz-card" onClick={() => setPage("talent")} data-testid="dashboard-biz-talent">
            <div className="k-biz-icon emerald"><Users size={20} /></div>
            <b>Discover Talent</b>
            <p>Search 3,400+ verified Indonesian technicians by skill, location, and reputation.</p>
            <span>Open talent search <ChevronRight size={13} /></span>
          </button>
          <button className="k-biz-card" onClick={() => setPage("sourcing")} data-testid="dashboard-biz-sourcing">
            <div className="k-biz-icon amber"><Workflow size={20} /></div>
            <b>Source & Compare Vendors</b>
            <p>Create an RFQ. KABELUL AI extracts and normalizes quotations, then recommends the best fit.</p>
            <span>Open sourcing <ChevronRight size={13} /></span>
          </button>
          <button className="k-biz-card" onClick={() => setPage("reputation")} data-testid="dashboard-biz-reputation">
            <div className="k-biz-icon blue"><Star size={20} /></div>
            <b>Reputation Feedback</b>
            <p>Completed work feeds back into technician & vendor reputation. Trust is compounding.</p>
            <span>See reputation <ChevronRight size={13} /></span>
          </button>
        </div>
      </section>
    </div>
  );
}
